import { ConfigProvider } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { UserProvider } from "./context/UserContext";
import { AppLayout } from "./components/AppLayout";
import { EnvironmentBanner } from "./components/EnvironmentBanner";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { GOOGLE_CLIENT_ID } from "./helpers/constants";

import LoginPage from "./pages/Login";
import HomePage from "./pages/HomePage";
import ModulePage from "./pages/ModulePage";
import SearchResultsPage from "./pages/SearchResultsPage";
import DashboardPage from "./pages/DashboardPage";
import ItemsPage from "./pages/masters/ItemsPage";
import CategoriesPage from "./pages/masters/CategoriesPage";
import UnitsPage from "./pages/masters/UnitsPage";
import TaxPoliciesPage from "./pages/masters/TaxPoliciesPage";
import SuppliersPage from "./pages/masters/SuppliersPage";
import BoatsPage from "./pages/masters/BoatsPage";
import ProjectsListPage from "./pages/projects/ProjectsListPage";
import ProjectDetailPage from "./pages/projects/ProjectDetailPage";
import BOMVersionDetailPage from "./pages/projects/BOMVersionDetailPage";
import BOMVariationPage from "./pages/projects/BOMVariationPage";
import RequestsListPage from "./pages/requests/RequestsListPage";
import RequestDetailPage from "./pages/requests/RequestDetailPage";
import IssuesListPage from "./pages/issues/IssuesListPage";
import IssueDetailPage from "./pages/issues/IssueDetailPage";
import BoatNotesListPage from "./pages/boat-notes/BoatNotesListPage";
import BoatNoteDetailPage from "./pages/boat-notes/BoatNoteDetailPage";
import ConsumptionListPage from "./pages/consumption/ConsumptionListPage";
import ConsumptionDetailPage from "./pages/consumption/ConsumptionDetailPage";
import StockRegisterPage from "./pages/stock/StockRegisterPage";
import StockLedgerPage from "./pages/stock/StockLedgerPage";
import TransfersListPage from "./pages/transfers/TransfersListPage";
import TransferDetailPage from "./pages/transfers/TransferDetailPage";
import ReturnsListPage from "./pages/returns/ReturnsListPage";
import ReturnDetailPage from "./pages/returns/ReturnDetailPage";
import SupplierReturnsListPage from "./pages/returns/SupplierReturnsListPage";
import SupplierReturnDetailPage from "./pages/returns/SupplierReturnDetailPage";
import MRNsListPage from "./pages/receipts/MRNsListPage";
import MRNDetailPage from "./pages/receipts/MRNDetailPage";
import DiscrepanciesListPage from "./pages/discrepancies/DiscrepanciesListPage";
import DiscrepancyDetailPage from "./pages/discrepancies/DiscrepancyDetailPage";
import AdjustmentsListPage from "./pages/adjustments/AdjustmentsListPage";
import AdjustmentDetailPage from "./pages/adjustments/AdjustmentDetailPage";
import ToolsListPage from "./pages/tools/ToolsListPage";
import ToolDetailPage from "./pages/tools/ToolDetailPage";
import PRsListPage from "./pages/procurement/PRsListPage";
import PRDetailPage from "./pages/procurement/PRDetailPage";
import RFQsListPage from "./pages/procurement/RFQsListPage";
import RFQDetailPage from "./pages/procurement/RFQDetailPage";
import POsListPage from "./pages/procurement/POsListPage";
import PODetailPage from "./pages/procurement/PODetailPage";
import POAmendmentDetailPage from "./pages/procurement/POAmendmentDetailPage";
import GRNsListPage from "./pages/grn/GRNsListPage";
import GRNDetailPage from "./pages/grn/GRNDetailPage";
import GINDirectsListPage from "./pages/gin-direct/GINDirectsListPage";
import GINDirectDetailPage from "./pages/gin-direct/GINDirectDetailPage";
import ReportsHubPage from "./pages/reports/ReportsHubPage";
import ProjectItemStatusPage from "./pages/reports/ProjectItemStatusPage";
import BomVsActualPage from "./pages/reports/BomVsActualPage";
import StockMovementReportPage from "./pages/reports/StockMovementReportPage";
import ProcurementStatusPage from "./pages/reports/ProcurementStatusPage";
import LowStockReportPage from "./pages/reports/LowStockReportPage";
import ReportPage from "./pages/reports/ReportPage";
import ApprovalsInboxPage from "./pages/approvals/ApprovalsInboxPage";
import StaffListPage from "./pages/staff/StaffListPage";
import StaffDetailPage from "./pages/staff/StaffDetailPage";
import WorkCalendarPage from "./pages/attendance/WorkCalendarPage";
import AttendanceRulesPage from "./pages/attendance/AttendanceRulesPage";
import AttendanceSheetsListPage from "./pages/attendance/AttendanceSheetsListPage";
import AttendanceSheetDetailPage from "./pages/attendance/AttendanceSheetDetailPage";
import ServiceRequestsListPage from "./pages/service-requests/ServiceRequestsListPage";
import ServiceRequestDetailPage from "./pages/service-requests/ServiceRequestDetailPage";
import VehiclesPage from "./pages/vehicles/VehiclesPage";
import UsersPage from "./pages/admin/UsersPage";
import RolesPage from "./pages/admin/RolesPage";
import WorkflowsPage from "./pages/admin/WorkflowsPage";
import StockLocationsPage from "./pages/admin/StockLocationsPage";
import DelegationsPage from "./pages/admin/DelegationsPage";
import AuditPage from "./pages/admin/AuditPage";
import DeveloperOptionsPage from "./pages/admin/DeveloperOptionsPage";
import TutorialsPage from "./pages/tutorials/TutorialsPage";
import { TutorialProvider } from "./tutorials/TutorialContext";

const queryClient = new QueryClient();

const glutTheme = {
  token: {
    colorPrimary: "#0f6b4c",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
};

export default function App() {
  const content = (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={glutTheme}>
        <EnvironmentBanner />
        <BrowserRouter>
          <UserProvider>
            <TutorialProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                {/* Card navigation: the landing page lists modules, /m/:moduleKey is the hub for one. */}
                <Route index element={<HomePage />} />
                <Route path="m/:moduleKey" element={<ModulePage />} />
                {/* The full result set behind the header palette (components/GlobalSearch). */}
                <Route path="search" element={<SearchResultsPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="approvals" element={<ApprovalsInboxPage />} />

                <Route path="masters/items" element={<ItemsPage />} />
                <Route path="masters/categories" element={<CategoriesPage />} />
                <Route path="masters/units" element={<UnitsPage />} />
                <Route path="masters/tax-policies" element={<TaxPoliciesPage />} />
                <Route path="masters/suppliers" element={<SuppliersPage />} />
                <Route path="masters/boats" element={<BoatsPage />} />

                <Route path="projects" element={<ProjectsListPage />} />
                <Route path="projects/:id" element={<ProjectDetailPage />} />
                <Route path="bom-versions/:id" element={<BOMVersionDetailPage />} />
                <Route path="bom-variations/:id" element={<BOMVariationPage />} />

                <Route path="requests" element={<RequestsListPage />} />
                <Route path="requests/:id" element={<RequestDetailPage />} />
                <Route path="issues" element={<IssuesListPage />} />
                <Route path="issues/:id" element={<IssueDetailPage />} />
                <Route path="boat-notes" element={<BoatNotesListPage />} />
                <Route path="boat-notes/:id" element={<BoatNoteDetailPage />} />
                <Route path="consumption" element={<ConsumptionListPage />} />
                <Route path="consumption/:id" element={<ConsumptionDetailPage />} />
                <Route path="transfers" element={<TransfersListPage />} />
                <Route path="transfers/:id" element={<TransferDetailPage />} />
                <Route path="returns" element={<ReturnsListPage />} />
                <Route path="returns/:id" element={<ReturnDetailPage />} />
                <Route path="supplier-returns" element={<SupplierReturnsListPage />} />
                <Route path="supplier-returns/:id" element={<SupplierReturnDetailPage />} />
                <Route path="receipts" element={<MRNsListPage />} />
                <Route path="receipts/:id" element={<MRNDetailPage />} />
                <Route path="discrepancies" element={<DiscrepanciesListPage />} />
                <Route path="discrepancies/:id" element={<DiscrepancyDetailPage />} />
                <Route path="adjustments" element={<AdjustmentsListPage />} />
                <Route path="adjustments/:id" element={<AdjustmentDetailPage />} />
                <Route path="tools" element={<ToolsListPage />} />
                <Route path="tools/:id" element={<ToolDetailPage />} />

                <Route path="stock" element={<StockRegisterPage />} />
                <Route path="stock/ledger" element={<StockLedgerPage />} />

                <Route path="procurement/prs" element={<PRsListPage />} />
                <Route path="procurement/prs/:id" element={<PRDetailPage />} />
                <Route path="procurement/rfqs" element={<RFQsListPage />} />
                <Route path="procurement/rfqs/:id" element={<RFQDetailPage />} />
                <Route path="procurement/pos" element={<POsListPage />} />
                <Route path="procurement/pos/:id" element={<PODetailPage />} />
                <Route path="procurement/pos/:poId/amendments/:id" element={<POAmendmentDetailPage />} />
                {/* An amendment reached from a notification or approval is only known by its own id. */}
                <Route path="procurement/amendments/:id" element={<POAmendmentDetailPage />} />
                <Route path="procurement/grns" element={<GRNsListPage />} />
                <Route path="procurement/grns/:id" element={<GRNDetailPage />} />
                <Route path="procurement/gin-directs" element={<GINDirectsListPage />} />
                <Route path="procurement/gin-directs/:id" element={<GINDirectDetailPage />} />

                <Route path="staff" element={<StaffListPage />} />
                <Route path="staff/:id" element={<StaffDetailPage />} />
                <Route path="work-calendar" element={<WorkCalendarPage />} />
                <Route path="attendance" element={<AttendanceSheetsListPage />} />
                <Route path="attendance/:id" element={<AttendanceSheetDetailPage />} />
                {/* Not `attendance/rules` - that would be swallowed by the `:id` detail route above. */}
                <Route path="attendance-rules" element={<AttendanceRulesPage />} />

                <Route path="service-requests" element={<ServiceRequestsListPage />} />
                <Route path="service-requests/:id" element={<ServiceRequestDetailPage />} />
                <Route path="vehicles" element={<VehiclesPage />} />

                <Route path="reports" element={<ReportsHubPage />} />
                <Route path="reports/project-item-status" element={<ProjectItemStatusPage />} />
                <Route path="reports/bom-vs-actual" element={<BomVsActualPage />} />
                <Route path="reports/stock-movement" element={<StockMovementReportPage />} />
                <Route path="reports/procurement-status" element={<ProcurementStatusPage />} />
                <Route path="reports/low-stock" element={<LowStockReportPage />} />
                {/* Every other §24 report is a straight tabular read, served by one registry-driven page. */}
                <Route path="reports/:key" element={<ReportPage />} />

                <Route path="admin/users" element={<UsersPage />} />
                <Route path="admin/roles" element={<RolesPage />} />
                <Route path="admin/workflows" element={<WorkflowsPage />} />
                <Route path="admin/stock-locations" element={<StockLocationsPage />} />
                <Route path="admin/delegations" element={<DelegationsPage />} />
                <Route path="admin/audit" element={<AuditPage />} />
                <Route path="admin/developer" element={<DeveloperOptionsPage />} />

                <Route path="tutorials" element={<TutorialsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </TutorialProvider>
          </UserProvider>
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  );

  return GOOGLE_CLIENT_ID ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{content}</GoogleOAuthProvider> : content;
}
